import { useCallback, useEffect, useState } from 'react'
import type { SpeedFence, SpeedSegment } from './types'
import { fetchSpeedSegments } from './useAdminApi'
import { AdminTable, Banner, SectionHeader, Td, Th } from './ui'

const ROUTE_LABELS: Record<string, string> = {
  'dhar-dhule-laden': 'Dhar → Dhule (laden)',
  'dhar-dhule-empty': 'Dhule → Dhar (empty)',
}

export function SpeedSection({ configured }: { configured: boolean }) {
  const [routes, setRoutes] = useState<Record<string, SpeedSegment[]>>({})
  const [fences, setFences] = useState<SpeedFence[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!configured) { setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      const data = await fetchSpeedSegments()
      setRoutes(data.routes)
      setFences(data.fences)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load speed segments')
    } finally {
      setLoading(false)
    }
  }, [configured])

  useEffect(() => { void load() }, [load])

  if (!configured) {
    return <p className="text-sm text-slate-500">The reporting database is not configured.</p>
  }

  const routeIds = Object.keys(routes).sort()
  const builtAt = routeIds
    .map((id) => routes[id]?.[0]?.built_at)
    .find((t): t is string => Boolean(t))

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Speed profile"
        subtitle="The per-kilometre limit actually in force on each route. Read-only — derived from road class, curvature, and the device speed ceiling; the lowest of the three wins."
      />
      {error ? <Banner tone="error">{error}</Banner> : null}
      {builtAt ? (
        <p className="text-xs text-slate-400">
          Last rebuilt {new Date(builtAt).toLocaleString('en-IN')}.
        </p>
      ) : null}
      {loading ? (
        <p className="text-sm text-slate-500">Loading…</p>
      ) : routeIds.length === 0 ? (
        <p className="text-sm text-slate-500">
          No speed profile on record yet.
        </p>
      ) : (
        routeIds.map((routeId) => (
          <div key={routeId}>
            <p className="mb-2 text-sm font-medium text-slate-900">
              {ROUTE_LABELS[routeId] ?? routeId}
            </p>
            <AdminTable>
              <thead>
                <tr>
                  <Th>From km</Th><Th>To km</Th><Th>Limit</Th><Th>Road</Th><Th>Source</Th>
                </tr>
              </thead>
              <tbody>
                {(routes[routeId] ?? []).map((s) => (
                  <tr key={s.seq}>
                    <Td className="font-mono">{s.from_km.toFixed(1)}</Td>
                    <Td className="font-mono">{s.to_km.toFixed(1)}</Td>
                    <Td className="font-mono">
                      {s.limit_kph != null ? `${s.limit_kph} kph` : '—'}
                    </Td>
                    <Td>
                      {s.highway_class ?? '—'}{s.road_ref ? ` · ${s.road_ref}` : ''}
                    </Td>
                    <Td className="text-slate-400 text-[12px]">{s.source ?? '—'}</Td>
                  </tr>
                ))}
              </tbody>
            </AdminTable>
          </div>
        ))
      )}
      {!loading && fences.length > 0 && (
        <div>
          <p className="mb-1 text-sm font-medium text-slate-900">Zone overrides</p>
          <p className="mb-2 text-xs text-slate-500">
            A geofence's own limit, set separately from the corridor profile above --
            OUTRANKS it for a vehicle inside that zone. This is where a "5 KMPH limit"
            OVERSPEED alert near a plant or a gate comes from.
          </p>
          <AdminTable>
            <thead>
              <tr>
                <Th>Zone</Th><Th>Type</Th><Th>Laden</Th><Th>Empty</Th>
              </tr>
            </thead>
            <tbody>
              {fences.map((f) => (
                <tr key={f.geofence_id}>
                  <Td>{f.name}</Td>
                  <Td className="text-slate-400 text-[12px]">{f.type}</Td>
                  <Td className="font-mono">
                    {f.speed_laden_kph != null ? `${f.speed_laden_kph} kph` : '—'}
                  </Td>
                  <Td className="font-mono">
                    {f.speed_empty_kph != null ? `${f.speed_empty_kph} kph` : '—'}
                  </Td>
                </tr>
              ))}
            </tbody>
          </AdminTable>
        </div>
      )}
    </div>
  )
}
